/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { dateCache, PullColumn } from '@touchplan/common';
import { SpritePool, TextPool } from '../../../../app-data/models/data-types/GenericPool';
import { HighResolutionText } from '@appCanvasCommon/text/HighResolutionText';
import { cssVar } from '@touchplan/css-var';
import {
	getPullSpaceStart,
	indicatorArrowFactory,
	timelineActiveTextPoolFactory,
	timelineTextPoolFactory,
} from '@appPlanApplication/timeline/TimelineHelpers';
import { FancySprite } from '@appCanvasCommon/graphics/FancySprite';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { takeUntil } from 'rxjs/operators';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { displayLineSettings } from '@appPlanApplication/timeline/AbstractDisplayLine';
import { ValuePool } from '../../../../app-data/models/data-types/ValuePool';
import { IPlanMilestone } from '../../../../modules/plan/services/milestone-line.service';

const settings = {
	dateColor: cssVar.white,
	dateFormat: `MMM DD YYYY`,
	textSize: 12,
	timelineHeight: 38,
};

export class PullSpaceTimelineDrawer extends AbstractAreaDrawer {
	/**
	 * We store the last Pull Columns as a cache to handle cases where the canvas is forced to redraw
	 * @private
	 */
	private _cachedPullColumns: PullColumn[] | null = null;
	/**
	 * We store the last Active Line as a cache to handle a special case from old visibility function that is intended
	 * to prevent the indicator arrows from drawing above the active line when scaled out.
	 * @private
	 */
	private _cachedActiveLine: ActiveLine | null = null;
	/**
	 * We store the milestone line data here to handle special drawing conditions for pull columns, like not drawing
	 * a pull column date if the pull column date matches the plan milestone date.
	 * @private
	 */
	private _cachedMilestoneLine!: IPlanMilestone | null;
	/**
	 * ValuePool contains an object pool of HighResolutionText object. The ValuePool will automatically recycle text objects
	 * as needed to ensure we are not always creating or destroying multiple objects as the pull space timeline is re-drawn
	 * @private
	 */
	private _textPool: ValuePool<HighResolutionText> = ValuePool.createTextValuePool(
		timelineActiveTextPoolFactory,
		this._container
	);
	/**
	 * SpritePool contains an object pool of FancySprite objects. It automatically recycles objects as needed to ensure
	 * we are not always creating or destroying multiple objects as the pull space timeline is re-drawn
	 * @private
	 */
	private _iconPool: SpritePool = new SpritePool(indicatorArrowFactory, this._container);
	/**
	 * Create a new instance of the PullSpaceTimelineDrawer. This Drawer is intended to be controlled by the
	 * PullSpaceDrawer because it prevents us from having to calculate the column spread multiple times
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.UI_TIMELINE_PULL_SPACE);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}
	/**
	 * Draw the Pull Space Timeline Visual. This includes:
	 * - Date text on the timeline
	 * - Orange triangle sprite indicator
	 * @param spreadedColumns PullColumn[] The pull columns to draw (after spread/nutella algorithm applied)
	 */
	public draw(spreadedColumns: PullColumn[] | null = this._cachedPullColumns): void {
		if (!spreadedColumns || !spreadedColumns.length) {
			this.hide();
			return;
		}
		this._cachedPullColumns = spreadedColumns;

		this.update();

		// We need to reset the text pool and sprite pool to reset internal pointers and refresh the screen with a new draw
		this._textPool.reset();
		this._iconPool.reset();

		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		spreadedColumns.forEach((column) => {
			// TP-7228: Don't draw the pull column in the timeline if it matches a plan milestone
			// otherwise the timeline gets very crowded and busy visually.
			if (column.plannedFinish === this._cachedMilestoneLine?.targetDate) {
				return;
			}
			this.createText(column);
			this.createIndicator(column);
		});
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(_?: number): void {
		if (!this._cachedPullColumns) {
			return;
		}

		const data: PullColumn[] = this._cachedPullColumns;
		// It's possible that the spreadedColumns is an empty array, so we terminate silently if so.
		// (We cannot position if there are no columns in the view)
		if (!data.length) {
			return;
		}
		const yOffset: number = settings.timelineHeight / 2;
		super.update(
			this.parentApplication.camera.realToScreenX(getPullSpaceStart(data)),
			cssVar.headerHeight_px + yOffset
		);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.sourceOnly): void {
		super.destroy(options);
		this._iconPool.destroy(DestroyOptions.sourceOnly);
		this._textPool.destroy(DestroyOptions.sourceOnly);
		this._textPool = null;
		this._iconPool = null;
		this._canvasAngularLand = null;
		this._cachedPullColumns = null;
		this._cachedActiveLine = null;
	}

	/**
	 * Create the timeline date text label.
	 * This is invoked as part of the draw() loop over the pull columns
	 * @param column
	 * @private
	 */
	private createText(column: PullColumn): void {
		// It is possible for pull columns to exist but not have a date, so we explicitly set an empty string if this the case.
		const text: string =
			column.plannedFinish !== undefined
				? dateCache.getFormat(column.plannedFinish, settings.dateFormat)
				: ``;
		// Terminate early so we don't create a pool that will only ever be empty text...
		if (text === ``) {
			return;
		}
		const textObject: HighResolutionText = this._textPool.grab(text) as HighResolutionText;
		textObject.style.fill = settings.dateColor;
		textObject.style.fontSize = settings.textSize;
		// Using 0 for the Y because we want the text to always draw at the same Y value relative to the container
		textObject.position.set(
			this.parentApplication.camera.realToScreenX(column.finishX) - this._container.x,
			0
		);
		textObject.visible = this.isVisible(column);
	}

	/**
	 * Create the orange triangle as a visual indicator of the pull columns
	 * @param column
	 * @private
	 */
	private createIndicator(column: PullColumn): void {
		const spriteObject: FancySprite = this._iconPool.grab() as FancySprite;
		spriteObject.position.set(
			this.parentApplication.camera.realToScreenX(column.finishX) - this._container.x,
			settings.timelineHeight / 2 + spriteObject.height / 2
		);
		spriteObject.visible = this.isVisible(column);
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.activeLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((line) => (this._cachedActiveLine = line));

		this._canvasAngularLand.milestoneLineService.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((milestone) => (this._cachedMilestoneLine = milestone));
	}

	/**
	 * This method handles special visibility logic. In this case, it copies old behavior where the pull column timeline
	 * label and indicator arrow should not appear if their screenX is less than the screenX of the activeLineX.
	 * The reason for this is otherwise the indicator arrow will draw above the active line, which is what we do not want.
	 * @param column
	 * @private
	 */
	private isVisible(column: PullColumn): boolean {
		if (!this._cachedActiveLine) {
			return true;
		}
		const columnScreenX: number = this.parentApplication.camera.realToScreenX(column.finishX);
		const activeLineScreenX: number =
			this.parentApplication.camera.realToScreenX(this._cachedActiveLine.view.activeLineX) +
			displayLineSettings.lineWidth;
		return columnScreenX > activeLineScreenX;
	}
}
